import React, { useState } from "react";
import { Card, Btn } from "../../components/UI";
import {
  Palette, Loader2, CheckCircle2, XCircle, AlertTriangle, ExternalLink,
  Monitor, Tv, Radio, HandCoins, ChevronDown, ChevronUp, FileUp,
} from "lucide-react";

export const CreativeDraftsCard = ({ creative }) => {
  const [tab, setTab] = useState("display");
  const [open, setOpen] = useState(true);
  if (!creative) return null;
  const tabs = [
    { key: "display", label: "Display", icon: Monitor },
    { key: "ctv", label: "CTV", icon: Tv },
    { key: "audio", label: "Audio", icon: Radio },
  ];
  return (
    <Card className="!p-4 border-fuchsia-400/25" data-testid="creative-drafts-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="toggle-creative-drafts">
        <span className="font-display font-semibold text-sm flex items-center gap-2">
          <Palette className="w-4 h-4 text-fuchsia-300" /> Creative drafts
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <>
          {creative.creative_notes && <p className="text-[11px] text-slate-500 mt-1">{creative.creative_notes}</p>}
          <div className="flex gap-1 mt-3">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`creative-tab-${t.key}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded-md border transition-colors ${
                  tab === t.key ? "border-fuchsia-400/40 bg-fuchsia-400/10 text-fuchsia-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                <t.icon className="w-3 h-3" /> {t.label}
              </button>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            {tab === "display" && (creative.display || []).map((d, i) => (
              <div key={i} className="rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid={`display-concept-${i}`}>
                <div className="text-xs font-semibold text-white">{d.headline}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{d.body}</div>
                <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-200 border border-fuchsia-400/25">{d.cta}</span>
              </div>
            ))}
            {tab === "ctv" && ["script_15", "script_30"].map((k) => creative.ctv?.[k] && (
              <div key={k} className="rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid={`ctv-${k}`}>
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-1">{k === "script_15" ? "15-second CTV spot" : "30-second CTV spot"}</div>
                <div className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap">{creative.ctv[k]}</div>
              </div>
            ))}
            {tab === "audio" && ["script_15", "script_30"].map((k) => creative.audio?.[k] && (
              <div key={k} className="rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid={`audio-${k}`}>
                <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold mb-1">{k === "script_15" ? "15-second audio spot" : "30-second audio spot"}</div>
                <div className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap">{creative.audio[k]}</div>
              </div>
            ))}
          </div>
          {creative.disclaimer && <div className="mt-2 text-[10px] text-slate-500 italic">Disclaimer on every asset: "{creative.disclaimer}"</div>}
        </>
      )}
    </Card>
  );
};

const STEP_ICON = {
  done: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-px" />,
  warn: <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-px" />,
  failed: <XCircle className="w-3.5 h-3.5 text-rose-300 shrink-0 mt-px" />,
};

export const LaunchChecklist = ({ steps }) => (
  <div className="mt-3 space-y-2" data-testid="launch-checklist">
    {steps.map((s) => (
      <div key={s.key} className="flex items-start gap-2" data-testid={`launch-step-${s.key}-${s.status}`}>
        {STEP_ICON[s.status] || <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300 shrink-0 mt-px" />}
        <div className="min-w-0">
          <div className={`text-[11px] ${s.status === "failed" ? "text-rose-200" : "text-slate-200"}`}>{s.label}</div>
          {s.detail && <div className="text-[10px] text-slate-500 mt-0.5">{s.detail}</div>}
        </div>
      </div>
    ))}
  </div>
);

export const FundraiserCard = ({ fundraiser }) => {
  if (!fundraiser) return null;
  return (
    <Card className="!p-4 border-emerald-400/25" data-testid="fundraiser-card">
      <div className="font-display font-semibold text-sm flex items-center gap-2">
        <HandCoins className="w-4 h-4 text-emerald-300" /> Fundraiser is live
      </div>
      <div className="mt-2 flex items-start gap-3">
        {fundraiser.qr && (
          <img src={fundraiser.qr} alt="Donation QR code" data-testid="fundraiser-qr"
            className="w-24 h-24 rounded-md bg-paper p-1 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white truncate">{fundraiser.name}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Goal ${Number(fundraiser.goal_amount || 0).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-1 break-all">{fundraiser.url}</div>
          <a href={fundraiser.url} target="_blank" rel="noreferrer" data-testid="fundraiser-open-link"
            className="inline-flex items-center gap-1 mt-2 text-[11px] text-emerald-300 hover:underline">
            <ExternalLink className="w-3 h-3" /> Open donation page
          </a>
        </div>
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Landing page, microsite and QR code created — the ad campaign clicks through to this page. Print the QR on mailers, podium signs and TV spots.</p>
    </Card>
  );
};

export const VoterFileCard = ({ wizard, voterFile, onUpload, uploading }) => {
  const [name, setName] = useState("");
  const [csvText, setCsvText] = useState(null);
  const [consent, setConsent] = useState(false);

  if (voterFile) {
    return (
      <Card className="!p-4 border-cyan-400/25" data-testid="voter-file-card">
        <div className="font-display font-semibold text-sm flex items-center gap-2">
          <FileUp className="w-4 h-4 text-cyan-300" /> Voter file connected
        </div>
        <div className="mt-2 text-xs text-slate-300">{voterFile.name}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{Number(voterFile.records || 0).toLocaleString()} hashed identifiers · {Number(voterFile.valid_emails || 0).toLocaleString()} valid emails</div>
        <div className={`mt-2 inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full border ${voterFile.activated ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/25" : "bg-amber-400/10 text-amber-300 border-amber-400/25"}`} data-testid="voter-file-status">
          {voterFile.activated ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {voterFile.activated ? "Activated as a private audience" : voterFile.note || "Not activated yet"}
        </div>
      </Card>
    );
  }
  if (!wizard?.active) return null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setName(f.name.replace(/\.csv$/i, ""));
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  return (
    <Card className="!p-4 border-cyan-400/30" data-testid="voter-file-wizard-card">
      <div className="font-display font-semibold text-sm flex items-center gap-2">
        <FileUp className="w-4 h-4 text-cyan-300" /> Upload your voter file
      </div>
      <p className="text-[11px] text-slate-500 mt-1">CSV with an <b>email</b> or <b>phone</b> column. Identifiers are hashed (SHA-256) before anything leaves this platform — raw contact info is never stored. Lists with 100+ valid emails activate as a private audience automatically.</p>
      <div className="mt-3 space-y-2.5">
        <label className="block cursor-pointer rounded-lg border-2 border-dashed border-white/15 hover:border-cyan-400/40 transition-colors p-4 text-center" data-testid="voter-file-dropzone">
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="voter-file-input" />
          <FileUp className="w-5 h-5 mx-auto text-slate-500" />
          <div className="text-xs text-slate-400 mt-1.5">{csvText ? `Ready: ${name}.csv` : "Click to choose a CSV file"}</div>
        </label>
        {csvText && (
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="List name"
            data-testid="voter-file-name-input"
            className="w-full bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-cyan-400/60 focus:outline-none" />
        )}
        <label className="flex items-start gap-2 text-[11px] text-slate-400 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-cyan-400" data-testid="voter-file-consent" />
          I confirm this list was collected lawfully and I'm authorized to use it for political outreach.
        </label>
        <Btn onClick={() => onUpload(name, csvText)} disabled={!csvText || !name.trim() || !consent || uploading} className="w-full" data-testid="voter-file-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
          {uploading ? "Hashing & activating…" : "Upload & activate"}
        </Btn>
      </div>
    </Card>
  );
};
